import React, { useCallback, useEffect, useState } from "react";
import MainLayout from "../../components/MainLayout";
import SessionService from "../../services/Session";
import { useUserSession } from "../../contexts/UserSessionContext";
import { useNavigate } from "react-router-dom";
import { homeRoutePath } from "../CoreRouter";
import { logAndFormatFormError } from "../../utils/Errors";
import { displayToastMessage } from "../../utils/Toasts";
import { useUsage } from "../../contexts/UsageContext";
import { usePlatforms } from "../../contexts/PlatformContext";
import GlobalLoading from "../../components/GlobalLoadingComponent";
import { Mixpanel } from "../../utils/Analytics";
import UsageDataList from "./UsageDataList";
import UsageFilterBar from "./UsageFilterBar";
import Pagination from "../../components/Pagination";
import { formatCurrency } from "../../utils/Currency";

// Read-only UsageManagement view
const UsageManagement: React.FC = () => {
  const navigate = useNavigate();
  const [isHandlingInitialLoad, setIsHandlingInitialLoad] =
    useState<boolean>(false);

  const { setUserSession, userSession, isAuthenticated } = useUserSession();
  const {
    usageRecords,
    loading,
    error,
    pagination,
    filters,
    setFilters,
    setPage,
    refreshUsageData,
  } = useUsage();

  const { platforms, refreshPlatforms } = usePlatforms();

  // Get total cost and records count for summary cards
  const totalCost = usageRecords.reduce(
    (sum, record) => sum + (record.total_cost || 0),
    0
  );
  const totalRecords = pagination.total || 0;

  // Handle initial loading of the dashboard
  const handleInitialLoad = useCallback(async () => {
    try {
      Mixpanel.track("UsageManagementView", {
        userId: userSession?.id,
      });

      setIsHandlingInitialLoad(true);

      const sessionService = new SessionService();

      // Check if the user is authenticated in our local context
      let sessionData: any = { userSessionData: userSession };
      if (!isAuthenticated) {
        sessionData = await sessionService.reviewSession();
        if (sessionData.success) {
          setUserSession(sessionData.userSessionData);
        } else {
          // If the user is not authenticated, redirect to home
          setUserSession(null);
          navigate(homeRoutePath, { replace: true });
          setIsHandlingInitialLoad(false);
          return;
        }
      }

      // Fetch platforms data
      try {
        await refreshPlatforms();
        await refreshUsageData();
      } catch (error) {
        const errorMessage = logAndFormatFormError(
          "UsageManagement.handleInitialLoad.refreshPlatforms",
          error,
          "Fetching platforms"
        );
        displayToastMessage("error", errorMessage);
      }

      // Fetch usage data - this happens via the UsageContext effect
      setIsHandlingInitialLoad(false);
    } catch (error: any) {
      // Suppress 401 errors (expected after logout)
      const status = error?.response?.status || error?.status;
      if (status !== 401) {
        const errorMessage = logAndFormatFormError(
        "UsageManagement.handleInitialLoad",
        error,
        "Handling initial load"
      );
      displayToastMessage("error", errorMessage);
      }
      navigate(homeRoutePath, { replace: true });
      setIsHandlingInitialLoad(false);
    }
  }, [
    userSession,
    isAuthenticated,
    setUserSession,
    navigate,
    refreshPlatforms,
    refreshUsageData,
  ]);

  useEffect(() => {
    handleInitialLoad();
  }, [handleInitialLoad]);

  const handleLogOut = useCallback(async () => {
    try {
      const sessionService = new SessionService();
      await sessionService.logOut();
      setUserSession(null);
      navigate(homeRoutePath, { replace: true });
    } catch (error) {
      const errorMessage = logAndFormatFormError(
        "UsageManagement.handleLogOut",
        error,
        "Logging out"
      );
      displayToastMessage("error", errorMessage);
    }
  }, [setUserSession, navigate]);

  const handleFilterChange = useCallback(
    (newFilters: Partial<typeof filters>) => {
      setFilters(newFilters);
    },
    [setFilters]
  );

  if (isHandlingInitialLoad) {
    return <GlobalLoading />;
  }

  if (error) {
    return (
      <div className="max-w-full mx-auto px-4 py-8">
        <div className="glass-card bg-gradient-to-r from-red-50 to-red-100 border border-red-200 rounded-3xl p-6 text-red-700 animate-slide-up">
          <h2 className="text-display text-lg font-semibold mb-2">Error</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  // Read-only list view
  return (
    <MainLayout handleLogOut={handleLogOut}>
      <div className="max-w-full mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-display text-2xl md:text-3xl font-bold text-apple-gray-900">
            Usage Management
          </h1>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="apple-card rounded-3xl shadow-apple p-6 border border-apple-gray-100 transition-shadow duration-300 hover:shadow-apple">
            <h3 className="text-apple-gray-600 text-sm font-medium mb-2">
              Total Usage Records
            </h3>
            <p className="text-3xl font-bold text-apple-gray-900">{totalRecords}</p>
          </div>

          <div className="apple-card rounded-3xl shadow-apple p-6 border border-apple-gray-100 transition-shadow duration-300 hover:shadow-apple">
            <h3 className="text-apple-gray-600 text-sm font-medium mb-2">
              Total Cost
            </h3>
            <p className="text-3xl font-bold text-apple-gray-900">
              {formatCurrency(totalCost || 0, {
                useSimpleFormat: true,
              })}
            </p>
          </div>
        </div>

        {/* Filter Bar */}
        <div className="apple-card rounded-3xl shadow-apple p-6 border border-apple-gray-100 mb-8">
          <UsageFilterBar
            platforms={platforms}
            filters={filters}
            onFilterChange={handleFilterChange}
          />
        </div>

        {/* Usage Records List */}
        <div className="apple-card rounded-3xl shadow-apple p-6 border border-apple-gray-100 mb-8">
          <h2 className="text-xl font-semibold text-apple-gray-900 mb-6">
            Usage Records
          </h2>

          {loading ? (
            <div className="py-10 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-apple-blue-500"></div>
              <p className="mt-2 text-apple-gray-600">Loading usage data...</p>
            </div>
          ) : usageRecords.length === 0 ? (
            <div className="py-10 text-center">
              <p className="text-apple-gray-600">No usage records found.</p>
            </div>
          ) : (
            <>
              <UsageDataList usageRecords={usageRecords} platforms={platforms} />

              {/* Pagination */}
              {pagination.pages > 1 && (
                <div className="mt-8">
                  <Pagination
                    currentPage={pagination.page}
                    totalPages={pagination.pages}
                    onPageChange={setPage}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default UsageManagement;
